// SaveCompareExport.js — Save projections to localStorage, compare, export CSV
import React, { useState, useEffect } from 'react';
import { Save, GitCompare, Download, Trash2 } from 'lucide-react';
import { generateCSV, downloadBlob } from '../../lib/projections/export';

const STORAGE_KEY = 'ff_saved_projections';

/**
 * Load saved projections from localStorage.
 */
function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Save projections to localStorage.
 */
function persistSaved(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

/**
 * Toolbar for save, compare, and export actions.
 * @param {{ result: Object, inputs: Object }} props
 */
const SaveCompareExport = ({ result, inputs }) => {
  const [saved, setSaved] = useState(loadSaved);
  const [showCompare, setShowCompare] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  useEffect(() => {
    setSaved(loadSaved());
  }, []);

  const handleSave = () => {
    if (!saveName.trim()) return;
    const entry = {
      id: Date.now().toString(),
      name: saveName.trim(),
      savedAt: new Date().toISOString(),
      inputs: { ...inputs },
      endValueHedged: result?.scenarios?.base?.endValueHedged || 0,
      annualized: result?.scenarios?.base?.annualizedReturnHedged || 0,
    };
    const updated = [...saved, entry];
    persistSaved(updated);
    setSaved(updated);
    setSaveName('');
    setShowSaveInput(false);
  };

  const handleDelete = (id) => {
    const updated = saved.filter(s => s.id !== id);
    persistSaved(updated);
    setSaved(updated);
  };

  const handleExportCSV = (scenarioKey = 'base') => {
    if (!result) return;
    const csv = generateCSV(result, scenarioKey);
    const filename = `projection_${scenarioKey}_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadBlob(csv, filename);
  };

  const fmtVal = (v) => {
    if (!v) return '--';
    const abs = Math.abs(v);
    if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5 mb-4">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-3">
        Actions
      </span>

      <div className="flex flex-wrap gap-2 mb-4">
        {/* Save */}
        <button
          onClick={() => setShowSaveInput(!showSaveInput)}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-none font-mono text-[10px] text-zinc-600 hover:bg-zinc-50"
        >
          <Save className="w-3 h-3" />
          Save
        </button>

        {/* Compare */}
        <button
          onClick={() => setShowCompare(!showCompare)}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-none font-mono text-[10px] text-zinc-600 hover:bg-zinc-50"
          disabled={saved.length === 0}
        >
          <GitCompare className="w-3 h-3" />
          Compare ({saved.length})
        </button>

        {/* Export CSV */}
        <button
          onClick={() => handleExportCSV('base')}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-none font-mono text-[10px] text-zinc-600 hover:bg-zinc-50"
        >
          <Download className="w-3 h-3" />
          Export CSV
        </button>

        {/* Export XLSX/PDF stubbed */}
        <span
          className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-100 rounded-none font-mono text-[10px] text-zinc-300 cursor-not-allowed"
          title="Coming soon"
        >
          <Download className="w-3 h-3" />
          XLSX / PDF (coming soon)
        </span>
      </div>

      {/* Save input */}
      {showSaveInput && (
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            placeholder="Projection name..."
            className="flex-1 font-mono text-[11px] px-2 py-1 border border-zinc-200 rounded-none text-zinc-700"
            onKeyDown={e => e.key === 'Enter' && handleSave()}
          />
          <button
            onClick={handleSave}
            disabled={!saveName.trim()}
            className="px-3 py-1 bg-zinc-600 text-white font-mono text-[10px] rounded-none hover:bg-zinc-700 disabled:bg-zinc-300 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      )}

      {/* Compare panel */}
      {showCompare && saved.length > 0 && (
        <div className="border-t border-zinc-100 pt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
            Saved Projections
          </p>
          <div className="space-y-2">
            {saved.map(s => (
              <div key={s.id} className="flex items-center justify-between bg-zinc-50 border border-zinc-100 p-2">
                <div>
                  <p className="font-mono text-[11px] text-zinc-700 font-medium">{s.name}</p>
                  <p className="font-mono text-[9px] text-zinc-400">
                    {new Date(s.savedAt).toLocaleDateString()} | End: {fmtVal(s.endValueHedged)} | Ann: {((s.annualized || 0) * 100).toFixed(2)}%
                  </p>
                  <p className="font-mono text-[9px] text-zinc-400">
                    Mode: {s.inputs?.mode} | Hedge: {((s.inputs?.hedgeRatio || 0) * 100).toFixed(0)}% | Tenor: {s.inputs?.tenorDays}d
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(s.id)}
                  className="p-1 text-zinc-400 hover:text-red-500"
                  title="Delete saved projection"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SaveCompareExport;
